import * as THREE from 'three';
import { REEF_ISLANDS } from '../sim/reef';
import { COASTLINE_KM } from '../sim/turtles';
import type { SealLife } from '../sim/seals';
import type { SealState } from '../sim/types';
import { MONK_SEAL_SPRITES } from '../ui/data/monkSealSprites';
import type { Sprite } from '../ui/data/spriteTypes';
import { FishBatch, facesRight, fishAtlas, type FishDraw } from './fishBatch';
import { hash3 } from './fishSwim';
import { pickNearest } from './pick';

/**
 * Maui Nui's monk seals in the whole tank (M145, the other session's option 4 the user chose: "do 4"): a sprite for
 * every `sealsPerSprite` seals, each on its island's coast at a depth it forages at, going back and forth along it.
 *
 * - `sealsPerSprite`: the other session's figure for the option, about 11 sprites of Maui Nui's 110 seals (JUDGED).
 * - `wanderUnits`, `wanderS`: how far along the coast either way it goes and how long the round takes (JUDGED, as the
 *   honu's `HONU_DRAW`).
 * - `gapShare`: how far over the floor it swims, a share of its height, over the highest floor under its length
 *   (JUDGED: near the bottom, where they feed; M150's review: over the floor under its middle alone, half the seals'
 *   frames on the coast's slope had its hillside end in the floor).
 * - `fadeS`: its fading in or out (JUDGED, the honu's).
 * - `adultUnits`: an adult's length, scene units, the younger in proportion (JUDGED: at the fish's scale, the honu's,
 *   0.0148 units a cm, an adult would be 3.08 units long, longer than the humpback's 2.4, `WHALE_DRAW`; drawn between a
 *   grown honu's sprite, 2.04 units across at a 90 cm shell and 2.27 at 100 cm, and the whale's, so the three keep
 *   their order of size; the user's to judge).
 */
export const SEAL_DRAW = { sealsPerSprite: 10, wanderUnits: 0.3, wanderS: 50, gapShare: 0.3, fadeS: 3, adultUnits: 2.35 } as const;

/**
 * A monk seal's standard length by its stage, m, and the depths they forage at, m.
 *
 * - Lengths: the 30 seals tagged at Laysan in October 2001, measured "to within 0.5 cm" (Stewart and Yochem 2004,
 *   PIFSC Admin. Rep. H-04-02C, p. 3; Table 1, p. 7, MEASURED): the weaned pups' (130, 128, 146, 132, 138, 144.5,
 *   144, 133, 132, 142 cm) mean 136.95 cm; the juveniles' of two and three years (159, 176.5, 150, 159, 151, 160, 169,
 *   165, 157, 174.5) 162.1; the adults' (195, 203, 211, 184, 218, 220, 208, 204, 215, 221) 207.9. The northwestern
 *   atolls' seals: no main islands' lengths by age were read.
 * - `forageM`: their dives on foraging trips, "deeper in depth (median = 19.4 m, IQR: 11.6–31.7 m)" (Wilson, Littnan
 *   and Read 2017, *Marine Mammal Science* 33, accepted manuscript p. 10, MEASURED in the main islands): the middle
 *   half, each sprite at an even draw across it.
 */
export const SEAL_BODY = { lengthM: { pup: 1.3695, juvenile: 1.621, adult: 2.079 }, forageM: [11.6, 31.7] as const };

export type SealStage = 'pup' | 'juvenile' | 'adult';

/**
 * Maui Nui's seals by stage on their state: pups those weaned this year since birth, juveniles those not yet of their
 * first pup's age, adults the rest; and the pups still nursing, ashore with their mothers, so never drawn at sea (M145's
 * review: "Nursing pups stay with their mothers at the haul-out").
 */
export function sealStages(life: SealLife, s: SealState): Record<SealStage | 'nursing', number> {
  let juvenile = 0;
  let adult = 0;
  s.counts.forEach((n, age) => {
    if (age === 0) return;
    if (age < life.firstPupYears) juvenile += n;
    else adult += n;
  });
  return { pup: s.counts[0] ?? 0, juvenile, adult, nursing: s.nursing.reduce((a, b) => a + b, 0) };
}

/**
 * A seal sprite: its stage, its place among its stage's sprites, its key (its stage's and its place's, so a sprite keeps
 * its stage and size from day to day as the honu's do; M150's review: numbered across the stages, one became another
 * as the rounding moved, popping in size), and its island (an index of `REEF_ISLANDS`).
 */
export interface SealSprite {
  stage: SealStage;
  index: number;
  key: number;
  island: number;
}

/**
 * The seals at sea's sprites (the nursing pups ashore not among them): one per `perSprite` seals, rounded (none for fewer than half a sprite's), shared among the
 * stages by their numbers (largest remainder), and each placed on an island by its coast's share of Maui Nui's (a
 * reading: they haul out and forage along every island's coast, the sim holding no island's own).
 */
export function sealSprites(life: SealLife, s: SealState, perSprite: number = SEAL_DRAW.sealsPerSprite): SealSprite[] {
  const stages = sealStages(life, s);
  const all = stages.pup + stages.juvenile + stages.adult;
  const n = Math.round(all / perSprite);
  if (n <= 0) return [];
  const order: SealStage[] = ['adult', 'juvenile', 'pup'];
  const exact = order.map((k) => (stages[k] / all) * n);
  const whole = exact.map(Math.floor);
  const left = n - whole.reduce((a, b) => a + b, 0);
  exact.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0] || a[1] - b[1]).slice(0, left).forEach(([, i]) => (whole[i] = (whole[i] ?? 0) + 1));
  const coast = REEF_ISLANDS.map((i) => COASTLINE_KM[i]);
  const total = coast.reduce((a, b) => a + b, 0);
  const out: SealSprite[] = [];
  order.forEach((stage, i) => {
    for (let index = 0; index < (whole[i] ?? 0); index++) {
      const key = i * 1000 + index;
      // Its island by its coast's share: a draw of its own, the same each day.
      let u = hash3(key, 3, 145) * total;
      let island = 0;
      while (island < coast.length - 1 && u >= (coast[island] ?? 0)) u -= coast[island++] ?? 0;
      out.push({ stage, index, key, island });
    }
  });
  return out;
}

/** The tank's water for the seals: as the honu's (`HonuWater`). */
export interface SealWater {
  x0: number;
  x1: number;
  z0: number;
  z1: number;
  top: number;
  floorY: (x: number, z: number) => number;
  depthOf: (y: number) => number;
  zOf: (island: number, u: number) => number | null;
}

/** A sprite's way: the points along its island's coast at its depth it goes back and forth between. */
interface SealWay {
  points: { x: number; z: number }[];
  phase: number;
}

const WAY_POINTS = 7;
/** Points along a seal's length its floor is read at, so it swims over the highest (as the honu's `lyingY`). */
const FOOT_POINTS = 5;
const ACROSS_STEPS = 400;

/** A seal sprite drawn: the sprite, its middle, its size, the way it faces, its fading (a tap's, M152). */
export interface SealDrawn {
  sprite: SealSprite;
  middle: THREE.Vector3;
  width: number;
  height: number;
  right: boolean;
  alpha: number;
}

/** The seals in the whole tank: their sprites in one batch, each going back and forth along its coast at its depth. */
export class ExhibitSeals {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects = new Map<SealStage, [number, number, number, number]>();
  private readonly ways = new Map<number, SealWay | null>();
  private sprites: SealSprite[] = [];
  /** Each sprite's fading by its index, and the sprite as last given, so one dropped fades out where it was (M145's review). */
  private alphas = new Map<number, number>();
  private last = new Map<number, SealSprite>();
  private lastAt = new Map<number, { x: number; z: number }>();
  private moved = new Map<number, { x: number; z: number }>();
  private facing = new Map<number, boolean>();
  private camera: THREE.Camera | null = null;
  private seconds = 0;
  private shown: SealDrawn[] = [];

  constructor(private readonly water: SealWater) {
    const stages: SealStage[] = ['adult', 'juvenile', 'pup'];
    const sprites = stages.map((k) => (MONK_SEAL_SPRITES[k] ?? MONK_SEAL_SPRITES.adult) as Sprite);
    const { texture, rects } = fishAtlas(sprites);
    stages.forEach((k, i) => this.rects.set(k, rects.get(sprites[i] as Sprite) as [number, number, number, number]));
    this.batch = new FishBatch(texture);
    // Drawn with the honu, over the fish, under the whales.
    this.batch.mesh.renderOrder = 4;
    this.group.add(this.batch.mesh);
  }

  /** A stage's drawn size: its sprite's width its length, the adult's `adultUnits`, the younger in proportion. */
  sizeOf(stage: SealStage): { width: number; height: number } {
    const sprite = (MONK_SEAL_SPRITES[stage] ?? MONK_SEAL_SPRITES.adult) as Sprite;
    const width = (SEAL_DRAW.adultUnits * SEAL_BODY.lengthM[stage]) / SEAL_BODY.lengthM.adult;
    return { width, height: (width * sprite.height) / sprite.width };
  }

  /** The depth a sprite forages at, m: an even draw of its own across the seals' foraging depths. */
  private depthOf(s: SealSprite): number {
    const [a, b] = SEAL_BODY.forageM;
    return a + hash3(s.key, 4, 145) * (b - a);
  }

  /** The highest floor under a sprite of `width` at a point (its `FOOT_POINTS` along its length). */
  private floorUnder(x: number, z: number, width: number): number {
    let floor = -Infinity;
    for (let f = 0; f < FOOT_POINTS; f++) floor = Math.max(floor, this.water.floorY(x + (f / (FOOT_POINTS - 1) - 0.5) * 0.9 * width, z));
    return floor;
  }

  /**
   * Where along x its coast's floor first reaches `depthM` at `z`, out from the land, and holds its whole body under the
   * surface over the floor under its length (M150's review: on a steep shore an adult held under the surface dipped its
   * shoreward end into the slope); null where it never does.
   */
  private acrossAt(z: number, depthM: number, size: { width: number; height: number }): number | null {
    const w = this.water;
    for (let i = 0; i <= ACROSS_STEPS; i++) {
      const x = w.x0 + (i / ACROSS_STEPS) * (w.x1 - w.x0);
      const y = w.floorY(x, z);
      if (y < w.top && w.depthOf(y) >= depthM && this.floorUnder(x, z, size.width) + size.height * (1 + SEAL_DRAW.gapShare) <= w.top) return x;
    }
    return null;
  }

  private wayOf(s: SealSprite): SealWay | null {
    const key = s.key;
    if (this.ways.has(key)) return this.ways.get(key) ?? null;
    const z0 = this.water.zOf(s.island, hash3(s.key, 1, 145));
    let way: SealWay | null = null;
    if (z0 !== null) {
      const depthM = this.depthOf(s);
      const points: { x: number; z: number }[] = [];
      for (let k = 0; k < WAY_POINTS; k++) {
        const z = Math.max(this.water.z0, Math.min(this.water.z1, z0 + ((2 * k) / (WAY_POINTS - 1) - 1) * SEAL_DRAW.wanderUnits));
        const x = this.acrossAt(z, depthM, this.sizeOf(s.stage));
        if (x !== null) points.push({ x, z });
      }
      if (points.length > 0) way = { points, phase: hash3(s.key, 2, 145) };
    }
    this.ways.set(key, way);
    return way;
  }

  /** The day's sprites (`sealSprites`): those beyond them fade out, those new fade in. */
  set(sprites: SealSprite[]): void {
    this.sprites = sprites;
    for (const s of sprites) {
      if (!this.alphas.has(s.key)) this.alphas.set(s.key, 0);
      this.last.set(s.key, s);
    }
  }

  /** Moved on by `seconds`; still when `calm`. */
  swim(seconds: number, calm = false): void {
    if (!calm) this.seconds += seconds;
    const live = new Set(this.sprites.map((s) => s.key));
    for (const [k, a] of this.alphas) {
      const next = live.has(k) ? Math.min(1, a + seconds / SEAL_DRAW.fadeS) : a - seconds / SEAL_DRAW.fadeS;
      if (next <= 0 && !live.has(k)) {
        this.alphas.delete(k);
        this.last.delete(k);
        this.lastAt.delete(k);
        this.moved.delete(k);
        this.facing.delete(k);
      }
      else this.alphas.set(k, next);
    }
    this.draw();
  }

  /** Each turned the way `camera` sees it swim (M142). */
  face(camera: THREE.Camera): void {
    this.camera = camera;
    this.draw();
  }

  private draw(): void {
    const draws: FishDraw[] = [];
    const shown: SealDrawn[] = [];
    for (const [key, alphaNow] of this.alphas) {
      const s = this.last.get(key);
      if (!s) continue;
      const way = this.wayOf(s);
      const alpha = alphaNow;
      if (!way || alpha <= 0) continue;
      const size = this.sizeOf(s.stage);
      const u = ((1 - Math.cos(2 * Math.PI * (this.seconds / SEAL_DRAW.wanderS + way.phase))) / 2) * (way.points.length - 1);
      const k = Math.min(Math.floor(u), Math.max(0, way.points.length - 2));
      const a = way.points[k] as { x: number; z: number };
      const b = way.points[k + 1] ?? a;
      const t = u - k;
      const x = a.x + (b.x - a.x) * t;
      const z = a.z + (b.z - a.z) * t;
      const floor = this.floorUnder(x, z, size.width);
      const y = Math.min(this.water.top - size.height / 2, floor + size.height * (0.5 + SEAL_DRAW.gapShare));
      const last = this.lastAt.get(key);
      const moved = last && Math.hypot(x - last.x, z - last.z) > 1e-9 ? { x: x - last.x, z: z - last.z } : this.moved.get(key) ?? { x: 1, z: 0 };
      const right = facesRight(this.camera, { x, y, z }, moved, this.facing.get(key) ?? moved.x > 0);
      this.lastAt.set(key, { x, z });
      this.moved.set(key, moved);
      this.facing.set(key, right);
      draws.push({ x, y: y - size.height / 2, z, ...size, rect: this.rects.get(s.stage) as [number, number, number, number], right, alpha });
      shown.push({ sprite: s, middle: new THREE.Vector3(x, y, z), ...size, right, alpha });
    }
    this.shown = shown;
    this.batch.set(draws);
  }

  /** The seals drawn now, for the tests. */
  drawn(): SealDrawn[] {
    return this.shown;
  }

  /** The seal under a point on the screen, its sprite's key; -1 for none (M152, the user's word: "i cant select monk seal in whole aquarium view"). */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): number {
    return pickNearest(this.shown, camera, x, y, widthPx, heightPx, withinPx)?.sprite.key ?? -1;
  }

  /** A seal as drawn now, by its sprite's key, or null where it is not drawn. */
  at(key: number): SealDrawn | null {
    return this.shown.find((s) => s.sprite.key === key) ?? null;
  }

  /** The sprites the day has, by stage, for the card. */
  counts(): Record<SealStage, number> {
    const out: Record<SealStage, number> = { pup: 0, juvenile: 0, adult: 0 };
    for (const s of this.sprites) out[s.stage]++;
    return out;
  }

  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  dispose(): void {
    this.batch.dispose();
  }
}
